import React from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { List } from '@fuww/library/src/List/index';
import { useIntl } from 'react-intl';
import {
  BrandListItem,
  BrandListItemContent,
} from '@fuww/library/src/List/BrandList';
import CategoryFilter from
'@fuww/library/src/ProductFilters/CategoryFilter';
import { DirectoryPage, DirectoryLayout } from '../components/DirectoryPage';
import ImageWithUrls from '../components/ImageWithUrls';
import withApollo from '../lib/apollo';
import brandsConnectionQuery
from '../lib/queries/products/brandsConnectionQuery.graphql';
import withQuery from '../lib/withQuery';
import loadMore from '../lib/queries/loadMore';
import messages from '../lib/messages.mjs';
import segments from '../lib/segments';
import LoadMore from '../components/LoadMore';
import LinkAndAnchor from '../components/LinkAndAnchor';
import redirectRouteToDomain from '../lib/redirectRouteToDomain';
import { directoryListImageConfigurations } from '../lib/imageConfigurations';
import replaceRoute from '../lib/replaceRoute';

export const headerImageUrls = {
  mobile: [
    'https://fashionunited.com/r/fit=cover,format=auto,gravity=center,height=34,quality=10,width=42/https://media.fashionunited.com/logos/brands_directory_default_image.hzAFjdMwPqGc5fgxy.jpg',
    'https://fashionunited.com/r/fit=cover,format=auto,gravity=center,height=288,quality=70,width=360/https://media.fashionunited.com/logos/brands_directory_default_image.hzAFjdMwPqGc5fgxy.jpg',
    'https://fashionunited.com/r/fit=cover,format=auto,gravity=center,height=576,quality=70,width=720/https://media.fashionunited.com/logos/brands_directory_default_image.hzAFjdMwPqGc5fgxy.jpg',
  ],
  desktop: [
    'https://fashionunited.com/r/fit=cover,format=auto,gravity=center,height=8,quality=10,width=42/https://media.fashionunited.com/logos/brands_directory_default_image.hzAFjdMwPqGc5fgxy.jpg',
    'https://fashionunited.com/r/fit=cover,format=auto,gravity=center,height=277,quality=70,width=1350/https://media.fashionunited.com/logos/brands_directory_default_image.hzAFjdMwPqGc5fgxy.jpg',
    'https://fashionunited.com/r/fit=cover,format=auto,gravity=center,height=526,quality=70,width=2560/https://media.fashionunited.com/logos/brands_directory_default_image.hzAFjdMwPqGc5fgxy.jpg',
    'https://fashionunited.com/r/fit=cover,format=auto,gravity=center,height=1052,quality=70,width=5120/https://media.fashionunited.com/logos/brands_directory_default_image.hzAFjdMwPqGc5fgxy.jpg',
  ],
};

const BrandsList = ({
  data, variables, fetchMore, loading,
}) => {
  const { brandsConnection: { edges, pageInfo: { hasNextPage } } } = data;

  return (
    <>
      <List className="fu-mdc-list">
        {edges && edges.map(({
          node: {
            id, name, profiles,
          },
        }) => {
          const premiumProfile = profiles.find(
            ({ page: { premium } }) => premium === true,
          );
          const {
            page: { slug, premium, industries },
            company: { country, logoUrls },
          } = premiumProfile || profiles[0];

          return (
            <BrandListItem key={id} premium={premium}>
              <LinkAndAnchor
                route="company-profile"
                params={{ slug }}
                // hoverUnderline
              >
                <BrandListItemContent
                  title={name}
                  industries={<>{industries.join(', ')}</>}
                  country={country}
                  logo={
                    logoUrls[0] && (
                      <ImageWithUrls
                        imageUrls={logoUrls}
                        title={name}
                        alt={name}
                        width="60px"
                        height="auto"
                        objectFit="cover"
                        sizes="60px"
                        srcSet={`${logoUrls[1]
                        } 60w, ${logoUrls[2]
                        } 120w, ${logoUrls[3]
                        } 180w`}
                        disableRoundedCorners
                      />
                    )
                  }
                  premium={premium}
                />
              </LinkAndAnchor>
            </BrandListItem>
          );
        })}
      </List>
      <LoadMore
        hasNextPage={hasNextPage}
        loading={loading}
        fetchMore={
          () => loadMore({ variables })(data, 'brandsConnection', fetchMore)
        }
      />
    </>
  );
};

BrandsList.propTypes = {
  variables: PropTypes.shape({}).isRequired,
  loading: PropTypes.bool.isRequired,
  fetchMore: PropTypes.func.isRequired,
  data: PropTypes.shape({
    brandsConnection: PropTypes.shape({
      pageInfo: PropTypes.shape({
        hasNextPage: PropTypes.bool,
      }),
      edges: PropTypes.arrayOf(
        PropTypes.shape({}),
      ),
    }),
  }).isRequired,
};

const BrandsListWithQuery = withQuery(BrandsList);

export const BrandsDirectoryPage = () => {
  const intl = useIntl();
  const { query } = useRouter();
  const {
    industries,
    country,
    section,
    search,
  } = query;

  const BRANDSLIMIT = 10;

  return (
    <DirectoryPage
      title={industries ? intl.formatMessage(
        messages['brands-directory-with-industry.title'], { industries },
      ) : intl.formatMessage(messages['brands-directory.title'])}
      route="brands-directory"
      pageType="brands"
      ComponentWithQuery={BrandsListWithQuery}
      query={brandsConnectionQuery}
      variables={{
        brandSearch: search,
        first: BRANDSLIMIT,
        type: 'COMPANY',
        hasProfile: true,
        hasPremiumProfile: section !== 'all' || null,
        country: country || null,
        industries,
        imageConfigurations: directoryListImageConfigurations,
        logoConfigurations: directoryListImageConfigurations,
      }}
      routeParams={industries ? {
        industries,
      } : {}}
      filters={(
        <CategoryFilter
          categories={
            [
              {
                name: 'All',
                handleInteraction: () => {
                  const { industries: _industries, ...rest } = query;

                  replaceRoute('brands-directory', rest);
                },
              },
              ...segments.map((categoryName) => ({
                name: categoryName,
                handleInteraction: () => replaceRoute(
                  'brands-directory', {
                    ...query,
                    industries: categoryName,
                  },
                ),
              }
              )),
            ]
          }
          selectedCategory={industries || 'All'}
          name="Filter"
        />
      )}
    />
  );
};

BrandsDirectoryPage.getLayout = (page) => (
  <DirectoryLayout
    imageUrls={headerImageUrls}
    alt="FashionUnited Brands Directory"
  >
    {page}
  </DirectoryLayout>
);

BrandsDirectoryPage.getInitialProps = redirectRouteToDomain('brands-directory');

export default withApollo(BrandsDirectoryPage);
